import React from "react";
import { DEFAULT_OUTLINE } from "@/lib/tov";

// Renders a provider's FULL SOAP structure from format_spec — not just section
// headings + layout, but the Subjective/Objective subsections and the numbered
// problem list (plan_subsections / assessment_style), honouring casing.

const applyCasing = (s, casing) => {
  if (!s) return s;
  if (casing === "UPPER") return s.toUpperCase();
  if (casing === "Title") return s.replace(/\w\S*/g, (w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase());
  if (casing === "Sentence") return s.charAt(0).toUpperCase() + s.slice(1).toLowerCase();
  return s;
};

const Chip = ({ children, testid }) => (
  <span data-testid={testid} className="inline-block text-[10.5px] font-medium text-[var(--ink2)] bg-black/[.05] rounded-full px-2.5 py-[3px] mr-1.5 mb-1.5">{children}</span>
);

export function FormatSpecView({ fs, dim }) {
  const order = fs?.section_order?.length ? fs.section_order : null;

  if (!order) {
    return (
      <div className={`rounded-[16px] border border-[#E9E9EC] bg-white p-5 ${dim ? "opacity-55" : ""}`} data-testid="note-format-outline">
        {DEFAULT_OUTLINE.map(([h, l]) => (
          <div key={h} className="mb-3 last:mb-0">
            <div className="text-[12.5px] font-bold text-[var(--ink)]">{h}</div>
            {l ? <div className="text-[10px] text-[var(--gray3)] mt-0.5">{l}</div> : null}
          </div>
        ))}
      </div>
    );
  }

  const casing = fs.casing;
  const planOwner = order.includes("plan") ? "plan" : order.includes("assessment") ? "assessment" : null;
  const subsFor = (k) => {
    if (k === "subjective") return fs.subjective_subsections || [];
    if (k === "objective") return fs.objective_subsections || [];
    if (k === planOwner) return fs.plan_subsections || [];
    return [];
  };

  return (
    <div className={`rounded-[16px] border border-[#E9E9EC] bg-white p-5 ${dim ? "opacity-55" : ""}`} data-testid="note-format-outline">
      {order.map((k, idx) => {
        const heading = applyCasing(fs.section_headings?.[k] || k.replace(/_/g, " "), casing);
        const layout = fs.layout?.[k];
        const subs = subsFor(k);
        const numbered = k === planOwner && fs.assessment_style === "numbered_problem_list";
        return (
          <div key={k} className={idx ? "mt-4 pt-4 border-t border-[#F0F0F2]" : ""} data-testid={`note-format-sec-${k}`}>
            <div className="flex items-center gap-2 flex-wrap">
              <div className="text-[12.5px] font-bold text-[var(--ink)]">{heading}</div>
              {layout ? <span className="text-[9.5px] font-semibold uppercase tracking-[.06em] text-[#5856D6] bg-[rgba(88,86,214,.09)] rounded-full px-2 py-[2px]">{layout}</span> : null}
              {k === planOwner && fs.assessment_style ? <span className="text-[9.5px] font-medium text-[var(--gray2)]">{fs.assessment_style.replace(/_/g, " ")}</span> : null}
            </div>
            {subs.length ? (
              numbered ? (
                <ol className="mt-2 ml-4 list-decimal text-[11.5px] text-[var(--gray1)] space-y-1" data-testid={`note-format-plan-${k}`}>
                  {subs.map((s, i) => <li key={i}>{s}</li>)}
                </ol>
              ) : (
                <div className="mt-2" data-testid={`note-format-subs-${k}`}>{subs.map((s, i) => <Chip key={i} testid={`note-format-sub-${k}-${i}`}>{s}</Chip>)}</div>
              )
            ) : null}
          </div>
        );
      })}
      {(fs.abbreviations || []).length ? (
        <div className="mt-4 pt-3 border-t border-[#E9E9EC] text-[10.5px] text-[var(--gray2)]">Abbreviations: {fs.abbreviations.join(", ")}</div>
      ) : null}
    </div>
  );
}
